import { rrulestr } from "rrule";

import User from "./User";
import Filters from "./filters";
import ApiHttp from "./ApiHttp";
import { generateFrequencyString } from "./Recurrence";
import { STATUS_TYPE_FROM_STATE } from "./dbbl/businessLogic/entities/dualApprovals";

import type { RRule } from "rrule";
import type Account from "./Account";

export type DualApprovalRequestProps = {
  uuid: string;
  created_at: Date;
  requester: User;
  responder: User | null;
  action:
    | DualApprovalRequestWireAction
    | DualApprovalRequestACHAction
    | DualApprovalRequestScheduledACHAction;
};

const isACHDualApproval = (
  dualApproval: API.AnyDualApproval,
): dualApproval is API.ACHDualApproval => {
  return (dualApproval as API.ACHDualApproval).ach_payment !== null;
};

const isScheduledACHDualApproval = (
  dualApproval: API.AnyDualApproval,
): dualApproval is API.ScheduledACHDualApproval => {
  return (
    (dualApproval as API.ScheduledACHDualApproval).scheduled_ach_payment !==
    null
  );
};

// common props between wire, ach and scheduled ACH
class DualApprovalRequestAction {
  type: "Wire" | "ACH" | "Scheduled ACH";

  state: string;

  dollarAmount: Dollars;

  displayType: "Wire" | "ACH";

  displayStatus: "approved" | "rejected" | "pending" | "expired";

  isOwnRequest: boolean;

  constructor(
    type: "Wire" | "ACH" | "Scheduled ACH",
    state: string,
    dollarAmount: Dollars,
    isOwnRequest: boolean,
  ) {
    this.type = type;
    this.state = state;
    this.dollarAmount = dollarAmount;
    this.displayType = this.getDisplayType();
    this.displayStatus = this.getDisplayStatus();
    this.isOwnRequest = isOwnRequest;
  }

  private getDisplayType() {
    if (this.type === "Wire") {
      return "Wire";
    }

    return "ACH";
  }

  private getDisplayStatus() {
    return STATUS_TYPE_FROM_STATE[
      this.state as keyof typeof STATUS_TYPE_FROM_STATE
    ];
  }

  isACH(): this is DualApprovalRequestACHAction {
    return this.type === "ACH";
  }

  isWire(): this is DualApprovalRequestWireAction {
    return this.type === "Wire";
  }

  isScheduledACH(): this is DualApprovalRequestScheduledACHAction {
    return this.type === "Scheduled ACH";
  }
}

class DualApprovalRequestWireAction extends DualApprovalRequestAction {
  // wire specific fields
  memo: string;

  templateName: API.Wire["template_name"];

  wireReason: API.Wire["wire_reason"];

  recipient: {
    name: API.Wire["beneficiary_name"];
    address: string;
    accountInstitution: API.Wire["to_account_institution_name"];
    accountNumber: API.Wire["to_account_number"];
    routingNumber: API.Wire["to_account_routing_number"];
  };

  fromAccountDisplay: API.Wire["from_account_display"];

  constructor(dualApproval: API.WireDualApproval, isOwnRequest: boolean) {
    const { wire } = dualApproval;
    const {
      amount,
      state,
      template_name,
      wire_reason,
      beneficiary_name,
      to_account_institution_name: accountInstitution,
      to_account_number: accountNumber,
      to_account_routing_number: routingNumber,
      from_account_display,
    } = wire;
    // call super to setup common dual approval request fields
    super("Wire", state, Filters.penniesToDollars(amount), isOwnRequest);

    this.memo = this.formatMemo(wire);
    this.templateName = template_name;
    this.wireReason = wire_reason;
    this.recipient = {
      name: beneficiary_name,
      address: this.formatAddress(wire),
      accountInstitution,
      accountNumber,
      routingNumber,
    };
    this.fromAccountDisplay = from_account_display;
  }

  private formatAddress(wire: API.Wire) {
    const addressParts = [
      wire.beneficiary_address_1,
      wire.beneficiary_address_2,
      wire.beneficiary_address_3,
    ];
    return addressParts.filter((line) => line).join("\n");
  }

  private formatMemo(wire: API.Wire) {
    const memoParts = [wire.memo_1, wire.memo_2, wire.memo_3, wire.memo_4];
    return memoParts.filter((line) => line).join("\n");
  }
}

export class DualApprovalRequestACHAction extends DualApprovalRequestAction {
  // ach & scheduled ach specific fields
  recipient: {
    name: API.Recipient["name"];
    accountNumber: API.ACHDestination["account_number"];
    routingNumber: API.ACHDestination["routing_number"];
    accountInstitution: API.ACHDestination["institution_name"];
    lastUpdatedAt: API.Recipient["updated_at"];
  };

  fromAccount?: Account;

  entryDescription:
    | API.ScheduledACHPayment["entry_desc"]
    | API.ACHPayment["entry_desc"];

  transactionType:
    | API.ScheduledACHPayment["std_ent_cls_code"]
    | API.ACHPayment["std_ent_cls_code"];

  constructor(
    dualApproval: API.ACHDualApproval | API.ScheduledACHDualApproval,
    isOwnRequest: boolean,
    recipients?: API.Recipient[],
    accounts?: Account[],
  ) {
    const type = isACHDualApproval(dualApproval) ? "ACH" : "Scheduled ACH";
    const payment = isACHDualApproval(dualApproval)
      ? dualApproval.ach_payment
      : dualApproval.scheduled_ach_payment;
    const state = isACHDualApproval(dualApproval)
      ? dualApproval.ach_payment?.state
      : dualApproval.scheduled_ach_payment?.dual_approval_state;

    const { amount } = payment;

    // call super to setup common dual approval request fields
    super(type, state, Filters.penniesToDollars(amount), isOwnRequest);
    const matchedRecipient = this.getRecipient(payment, recipients);
    const fromAccount = this.getAccount(payment, accounts);

    const {
      name = "",
      ach_destination: achDestination = {} as API.Recipient["ach_destination"],
      updated_at: updatedAt = "",
    } = matchedRecipient || {};

    this.recipient = {
      name,
      accountNumber: achDestination?.account_number || "",
      routingNumber: achDestination?.routing_number || "",
      accountInstitution: achDestination?.institution_name || "",
      lastUpdatedAt: updatedAt || "",
    };

    this.fromAccount = fromAccount;
    this.entryDescription = payment.entry_desc || "";
    this.transactionType = payment.std_ent_cls_code || "";
  }

  private getRecipient(
    payment: API.ScheduledACHPayment | API.ACHPayment,
    recipients?: API.Recipient[],
  ) {
    return recipients?.find((recipient) => recipient.id === payment.recipient);
  }

  private getAccount(
    payment: API.ScheduledACHPayment | API.ACHPayment,
    accounts?: Account[],
  ) {
    return accounts?.find(
      (account: Account) => account.id === payment.institution_account,
    );
  }
}

export class DualApprovalRequestScheduledACHAction extends DualApprovalRequestACHAction {
  // scheduled ach specific fields
  note: API.ScheduledACHPayment["memo"];

  recurringRule: string;

  rrule: RRule;

  scheduledDate: Date;

  recurrence: number | null;

  frequency: string;

  constructor(
    dualApproval: API.ScheduledACHDualApproval,
    isOwnRequest: boolean,
    recipients?: API.Recipient[],
    accounts?: Account[],
  ) {
    // call super to setup basic ACH fields
    super(dualApproval, isOwnRequest, recipients, accounts);

    this.note = dualApproval.scheduled_ach_payment.memo || "";
    this.recurringRule = dualApproval.scheduled_ach_payment.recurring_rule;
    this.rrule = this.getRrule();
    this.scheduledDate = this.getScheduledDate();
    this.recurrence = this.getRecurrence();
    this.frequency = this.getFrequency();
  }

  private getRrule() {
    return rrulestr(this.recurringRule);
  }

  private getScheduledDate() {
    return this.rrule.options.dtstart;
  }

  private getRecurrence() {
    let recurrence = null;
    // user inputted "count" (ends after X amount of payments)
    if (this.rrule.options.count) {
      recurrence = this.rrule.options.count;
      // there's an end date, so we have to calculate the count
    } else if (this.rrule.options.until) {
      recurrence = this.rrule.count();
    }

    // if infinite payment, it will return null
    return recurrence;
  }

  private getFrequency() {
    if ((this.recurrence && this.recurrence > 1) || !this.recurrence) {
      const frequency = generateFrequencyString(this.rrule);
      return frequency;
    }
    return "";
  }
}

export default class DualApprovalRequest {
  public uuid: DualApprovalRequestProps["uuid"];

  public created_at: DualApprovalRequestProps["created_at"];

  public requester: DualApprovalRequestProps["requester"];

  public responder: DualApprovalRequestProps["responder"];

  public action: DualApprovalRequestProps["action"];

  constructor(props: DualApprovalRequestProps) {
    this.uuid = props.uuid;
    this.created_at = props.created_at;
    this.requester = props.requester;
    this.responder = props.responder;
    this.action = props.action;
  }

  approve() {
    const payload = { action: "approve" };
    return ApiHttp.fetch(
      `approval_requests/${this.uuid}`,
      { method: "POST" },
      payload,
    ).then((response) => {
      const approvalRequest =
        response.approval_request.ach_payment ||
        response.approval_request.wire ||
        response.approval_request.scheduled_ach_payment;
      if (response.approval_request.scheduled_ach_payment) {
        this.action.state = approvalRequest.dual_approval_state;
      } else {
        this.action.state = approvalRequest.state;
      }
      return response;
    });
  }

  reject() {
    const payload = { action: "reject" };
    return ApiHttp.fetch(
      `approval_requests/${this.uuid}`,
      { method: "POST" },
      payload,
    ).then((response) => {
      const approvalRequest =
        response.approval_request.ach_payment ||
        response.approval_request.wire ||
        response.approval_request.scheduled_ach_payment;
      if (response.approval_request.scheduled_ach_payment) {
        this.action.state = approvalRequest.dual_approval_state;
      } else {
        this.action.state = approvalRequest.state;
      }
      return response;
    });
  }

  static deserialize(
    payload: API.AnyDualApproval,
    recipients?: API.Recipient[],
    accounts?: Account[],
    currentUserId?: API.UserId,
  ) {
    let action;
    const isOwnRequest = currentUserId === payload.requester.id;

    if (isScheduledACHDualApproval(payload)) {
      action = new DualApprovalRequestScheduledACHAction(
        payload,
        isOwnRequest,
        recipients,
        accounts,
      );
    } else if (isACHDualApproval(payload)) {
      action = new DualApprovalRequestACHAction(
        payload,
        isOwnRequest,
        recipients,
        accounts,
      );
    } else {
      action = new DualApprovalRequestWireAction(
        payload as API.WireDualApproval,
        isOwnRequest,
      );
    }

    return new DualApprovalRequest({
      uuid: payload.uuid,
      created_at: new Date(payload.created_at),
      requester: User.deserialize(payload.requester),
      responder: payload.responder ? User.deserialize(payload.responder) : null,
      action,
    });
  }
}
